import React, { useState } from 'react';
import { useEVM } from '../contexts/EVMProvider';
import { Wallet, LogOut, Copy, Check } from 'lucide-react';
import EVMWalletConnector from './EVMWalletConnector';

const EVMWalletButton = () => {
  const { 
    EVM_CHAINS, 
    selectedEVMChain,
    evmAccount,
    evmBalance,
    disconnectEVMWallet
  } = useEVM();
  const [showDropdown, setShowDropdown] = useState(false);
  const [copied, setCopied] = useState(false);

  const walletAddress = evmAccount;
  const isConnected = !!evmAccount;

  const handleDisconnect = () => {
    disconnectEVMWallet();
    setShowDropdown(false);
  };

  const copyAddress = async () => {
    if (walletAddress) {
      await navigator.clipboard.writeText(walletAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getChainInfo = () => {
    const chain = EVM_CHAINS[selectedEVMChain];
    return {
      name: chain?.name || 'Ethereum',
      symbol: chain?.symbol || 'ETH',
      color: 'text-blue-400',
    };
  };

  const chainInfo = getChainInfo();

  if (isConnected) {
    return (
      <div className="relative">
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          className="flex items-center space-x-2 bg-gradient-to-r from-gray-700 to-gray-800 hover:from-gray-600 hover:to-gray-700 text-white px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl font-medium transition-all duration-300 shadow-lg hover:shadow-xl text-sm sm:text-base min-h-[44px] sm:min-h-[48px]"
        >
          <Wallet className="w-4 h-4" />
          <span className="hidden sm:block">
            {walletAddress?.slice(0, 6)}...{walletAddress?.slice(-4)}
          </span>
          <span className="sm:hidden">Connected</span>
        </button>

        {showDropdown && (
          <div className="absolute top-full right-0 mt-2 w-72 sm:w-80 bg-gray-800 border border-gray-600 rounded-lg shadow-xl z-[9999]">
            <div className="p-3 sm:p-4">
              {/* Chain Info */}
              <div className="flex items-center justify-between mb-3 sm:mb-4 pb-3 border-b border-gray-600">
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${chainInfo.color.replace('text-', 'bg-')}`}></div>
                  <span className="text-white font-medium text-sm sm:text-base">{chainInfo.name}</span>
                </div>
                <span className="text-gray-400 text-xs sm:text-sm">{chainInfo.symbol}</span>
              </div>

              {/* Balance */}
              {evmBalance && (
                <div className="mb-3 sm:mb-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 text-xs sm:text-sm">Balance</span>
                  </div>
                  <div className="mt-1 p-2 sm:p-3 bg-gray-700/50 rounded text-xs sm:text-sm font-mono text-gray-300">
                    {parseFloat(evmBalance).toFixed(4)} {chainInfo.symbol}
                  </div>
                </div>
              )}

              {/* Address */}
              <div className="mb-3 sm:mb-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-400 text-xs sm:text-sm">Address</span>
                  <button
                    onClick={copyAddress}
                    className="flex items-center space-x-1 text-gray-400 hover:text-white transition-colors duration-300 p-1 rounded hover:bg-gray-700/50 min-h-[28px] min-w-[28px]"
                  >
                    {copied ? (
                      <Check className="w-3 h-3 sm:w-4 sm:h-4 text-green-400" />
                    ) : (
                      <Copy className="w-3 h-3 sm:w-4 sm:h-4" />
                    )}
                  </button>
                </div>
                <div className="mt-1 p-2 sm:p-3 bg-gray-700/50 rounded text-xs sm:text-sm font-mono text-gray-300 break-all">
                  {walletAddress}
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-2">
                <button
                  onClick={handleDisconnect}
                  className="w-full flex items-center justify-center space-x-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 hover:text-red-300 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg transition-all duration-300 text-sm sm:text-base min-h-[40px]"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Disconnect</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Not connected - show EVM wallet connector
  return <EVMWalletConnector />;
};

export default EVMWalletButton;

